import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';

export default function RitualPage({ data, onComplete }) {
  const { content } = data;
  const [ripples, setRipples] = useState([]);
  const textControls = useAnimation();
  const paperControls = useAnimation();
  const boatControls = useAnimation();
  const environmentControls = useAnimation();
  const isSkipped = useRef(false);

  const handleSkip = () => {
    isSkipped.current = true;
    onComplete();
  };

  useEffect(() => {
    let isMounted = true;
    let rippleInterval;

    const runRitual = async () => {
      // 初始狀態設定
      boatControls.set({ y: 0, scale: 0, opacity: 0 });
      environmentControls.set({ opacity: 0 });
      paperControls.set({ scale: 1, rotateX: 0, rotateY: 0, opacity: 1 });
      textControls.set({ opacity: 1 });

      // 1. 環境變暗，浮現月亮與湖面
      environmentControls.start({ opacity: 1, transition: { duration: 2.5, ease: "easeInOut" } });

      // 2. 摺紙動畫
      if (isSkipped.current || !isMounted) return;
      textControls.start({ opacity: 0, transition: { duration: 1.5 } });
      
      await paperControls.start({ 
        rotateX: 60, scale: 0.8, y: 20, 
        transition: { duration: 1.5, ease: "easeInOut" } 
      });

      if (isSkipped.current || !isMounted) return;
      await paperControls.start({ 
        scaleX: 0.1, rotateZ: 90, opacity: 0, 
        transition: { duration: 1.2, ease: "easeInOut" } 
      });

      if (isSkipped.current || !isMounted) return;
      await boatControls.start({ 
        y: 0, scale: 1, opacity: 1, rotateZ: [-5, 5, 0],
        transition: { duration: 0.8, ease: "easeOut" } 
      });

      if (isSkipped.current || !isMounted) return;
      
      let rippleCount = 0;
      rippleInterval = setInterval(() => {
        if (!isMounted || isSkipped.current) return;
        setRipples(prev => [...prev.slice(-2), rippleCount++]);
      }, 2000);

      setTimeout(() => {
        clearInterval(rippleInterval);
      }, 7000);

      // 5. 駛入迷霧：往畫面深處移動並縮小
      // 使用 relative viewport height (-35vh) 確保在大螢幕 (Mac/PC) 也能完美消失於地平線
      await boatControls.start({ 
        y: "-35vh", 
        scale: 0.02, 
        x: [0, 20, -15, 10, -5, 0],
        opacity: [1, 1, 0.8, 0.3, 0],
        transition: { 
          duration: 10.0, 
          ease: "easeInOut",
          x: { duration: 10.0, ease: "easeInOut", times: [0, 0.2, 0.4, 0.6, 0.8, 1] }
        } 
      });

      if (isSkipped.current || !isMounted) return;
      setTimeout(() => {
        if (isMounted && !isSkipped.current) onComplete();
      }, 800);
    };

    runRitual();

    return () => {
      isMounted = false;
      clearInterval(rippleInterval);
    };
  }, [textControls, paperControls, boatControls, environmentControls, onComplete]);

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden bg-[#FDFBF7]">
      {/* 1. 環境背景 */}
      <motion.div animate={environmentControls} className="absolute inset-0 z-0 flex flex-col">
        <div className="absolute top-0 w-full h-[50%] bg-gradient-to-b from-[#050B14] via-[#0A1220] to-[#131F33]">
          {/* 適配不同螢幕的月亮大小 */}
          <div className="absolute top-[15%] md:top-[20%] left-1/2 -translate-x-1/2 w-10 h-10 md:w-14 md:h-14 bg-[#FDFBF7] rounded-full shadow-[0_0_60px_rgba(253,251,247,0.9)] z-10"></div>
        </div>
        <div className="absolute top-[40%] w-full h-[20%] bg-gradient-to-b from-transparent via-[#131F33] to-transparent z-20 blur-md opacity-90 pointer-events-none"></div>
        <div className="absolute top-[50%] w-full h-[50%] bg-gradient-to-b from-[#131F33] to-[#050B14]">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 md:w-48 h-full bg-gradient-to-b from-white/10 to-transparent blur-2xl pointer-events-none"></div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 md:w-16 h-[60%] bg-gradient-to-b from-white/20 to-transparent blur-md pointer-events-none mix-blend-overlay"></div>
        </div>
      </motion.div>

      {/* 2. 動畫主體區塊：使用相對高度適配各種螢幕 */}
      <div className="relative w-full h-[50vh] md:h-[600px] mt-[10vh] md:mt-[15vh] flex justify-center items-center z-10 perspective-[1000px]">
        {/* 紙張本體 */}
        <motion.div 
          animate={paperControls}
          className="absolute w-56 md:w-64 aspect-[3/4] bg-paper shadow-2xl p-5 md:p-6 overflow-hidden rounded-sm z-20 flex flex-col transform-gpu"
        >
          <motion.div 
            animate={textControls}
            className="text-ink-brown font-handwriting text-xs md:text-sm leading-relaxed"
          >
            {content.length > 100 ? content.substring(0, 100) + '...' : content}
          </motion.div>
        </motion.div>

        {/* 紙船與水面 */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
          <div className="absolute top-1/2 left-1/2 w-0 h-0 flex items-center justify-center">
            {ripples.map(id => (
              <div key={id} className="ripple" />
            ))}
          </div>
          <motion.div 
            animate={boatControls}
            className="relative w-28 h-28 md:w-32 md:h-32 flex items-center justify-center transform-gpu origin-bottom"
          >
            <svg viewBox="0 0 100 60" className="w-full h-full drop-shadow-[0_15px_15px_rgba(0,0,0,0.6)]">
              <polygon points="50,8 75,38 50,38" fill="#B0B8C0" />
              <polygon points="50,5 25,38 50,38" fill="#E8EDF2" />
              <polygon points="8,38 92,38 68,52 32,52" fill="#8C96A0" />
              <polygon points="8,38 50,46 92,38 68,52 32,52" fill="#D0D8E0" />
            </svg>
          </motion.div>
        </div>
      </div>

      {/* 3. 跳過動畫按鈕 */}
      <button 
        onClick={handleSkip}
        className="absolute bottom-6 md:bottom-8 right-6 md:right-12 z-50 text-white/40 hover:text-white/80 font-handwriting tracking-widest text-sm transition-colors duration-300 px-4 py-2"
      >
        跳過動畫 &gt;&gt;
      </button>
    </div>
  );
}